// ============================================
// FILE: stateManagers/services/authService.ts
// ============================================
import axios from 'axios';
import Cookies from 'js-cookie';
import { getApiBaseUrl } from '../apiSetup/apiConfig';

const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
} as const;

// Custom error class for auth errors
export class AuthError extends Error {
  constructor(message: string, public statusCode?: number, public originalError?: any) {
    super(message);
    this.name = 'AuthError';
  }
}

export const login = async (email: string, password: string) => {
  console.log('🔐 Attempting login for:', email);
  
  const API_BASE_URL = getApiBaseUrl();
  console.log('🌐 Using API URL:', API_BASE_URL);
  
  try {
    const response = await axios.post(`${API_BASE_URL}/login`, {
      email,
      password,
    });

    console.log('📦 Full API Response:', response);
    console.log('📦 Response Data:', response.data);
    console.log('📦 Response Status:', response.status);

    // Check if response data exists
    if (!response.data) {
      console.error('❌ Empty response data received');
      throw new AuthError('Server returned an empty response. Please try again.');
    }

    const { success, access_token, refresh_token, user } = response.data;

    console.log('📊 Response structure:', {
      success,
      hasAccessToken: !!access_token,
      hasRefreshToken: !!refresh_token,
      hasUser: !!user
    });

    // Check if the API returned an error
    if (!success) {
      console.error('❌ API returned error status');
      throw new AuthError('Login failed. Please check your credentials.');
    }

    console.log('🔑 Access Token:', access_token ? '✓ Present' : '✗ Missing');
    console.log('🔄 Refresh Token:', refresh_token ? '✓ Present' : '✗ Missing');

    // Validate tokens exist
    if (!access_token || !refresh_token) {
      console.error('❌ Missing tokens in response.data:', {
        hasAccessToken: !!access_token,
        hasRefreshToken: !!refresh_token,
        success,
        fullResponse: response.data
      });
      
      // Provide specific error message based on what's missing
      if (!access_token && !refresh_token) {
        throw new AuthError('Authentication failed: Server did not return login tokens. Please contact your administrator.');
      } else if (!access_token) {
        throw new AuthError('Authentication failed: Access token is missing. Please contact your administrator.');
      } else {
        throw new AuthError('Authentication failed: Refresh token is missing. Please contact your administrator.');
      }
    }

    // Validate token format (basic check)
    if (typeof access_token !== 'string' || typeof refresh_token !== 'string') {
      console.error('❌ Invalid token format:', { 
        accessTokenType: typeof access_token, 
        refreshTokenType: typeof refresh_token 
      });
      throw new AuthError('Authentication failed: Invalid token format received from server.');
    }

    // Validate token is not empty
    if (access_token.trim() === '' || refresh_token.trim() === '') {
      console.error('❌ Empty token value');
      throw new AuthError('Authentication failed: Empty token received from server.');
    }

    // Save tokens in cookies
    try {
      Cookies.set('accessToken', access_token, COOKIE_OPTIONS);
      Cookies.set('refreshToken', refresh_token, COOKIE_OPTIONS);
      
      // Verify cookies were set
      const verifyAccessToken = Cookies.get('accessToken');
      const verifyRefreshToken = Cookies.get('refreshToken');
      
      console.log('🍪 Cookie verification:', {
        accessToken: verifyAccessToken ? 'SAVED' : 'FAILED',
        refreshToken: verifyRefreshToken ? 'SAVED' : 'FAILED'
      });
      
      if (!verifyAccessToken || !verifyRefreshToken) {
        console.error('❌ Failed to save tokens to cookies');
        throw new AuthError('Failed to save authentication data. Please check your browser settings and enable cookies.');
      }
      
      console.log('✅ Login successful – tokens saved to cookies');
      console.log('👤 User data:', user);
    } catch (cookieError) {
      console.error('❌ Cookie storage error:', cookieError);
      throw new AuthError('Failed to store authentication data. Please enable cookies in your browser.');
    }

    return response;
  } catch (error: any) {
    console.error('❌ Login failed - Full Error:', error);
    console.error('❌ Error Response:', error.response);
    console.error('❌ Error Response Data:', error.response?.data);
    console.error('❌ Error Response Status:', error.response?.status);
    console.error('❌ Error Message:', error.message);

    // If it's already an AuthError, throw it as-is
    if (error instanceof AuthError) {
      throw error;
    }

    // Handle axios errors
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const responseData = error.response?.data;

      // Check if API returned error in response body
      if (responseData?.error) {
        const errorMsg = responseData.error;
        console.error('❌ API error message:', errorMsg);
        throw new AuthError(errorMsg, status, error);
      }

      // Handle specific HTTP status codes
      if (status === 400) {
        throw new AuthError(
          responseData?.error || 'Invalid request. Please check your input.',
          status,
          error
        );
      } else if (status === 401) {
        throw new AuthError(
          responseData?.error || 'Invalid email or password. Please try again.',
          status,
          error
        );
      } else if (status === 403) {
        throw new AuthError(
          'Access denied. Your account may be disabled. Please contact your administrator.',
          status,
          error
        );
      } else if (status === 404) {
        throw new AuthError(
          'Login service not found. Please verify your configuration and try again.',
          status,
          error
        );
      } else if (status === 422) {
        throw new AuthError(
          responseData?.error || 'Invalid login credentials format. Please check your input.',
          status,
          error
        );
      } else if (status === 429) {
        throw new AuthError(
          'Too many login attempts. Please try again later.',
          status,
          error
        );
      } else if (status && status >= 500) {
        throw new AuthError(
          'Server error occurred. Please try again later or contact support.',
          status,
          error
        );
      } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        throw new AuthError(
          'Connection timeout. Please check your internet connection and try again.',
          undefined,
          error
        );
      } else if (error.code === 'ERR_NETWORK') {
        throw new AuthError(
          'Network error. Please check your internet connection and try again.',
          undefined,
          error
        );
      } else if (error.code === 'ERR_BAD_REQUEST') {
        throw new AuthError(
          responseData?.error || 'Invalid request. Please check your credentials.',
          status,
          error
        );
      } else if (responseData?.error) {
        throw new AuthError(responseData.error, status, error);
      }
    }

    // Generic fallback error
    throw new AuthError(
      error.message || 'Login failed. Please check your credentials and try again.',
      undefined,
      error
    );
  }
};

export const refreshToken = async (refreshToken: string) => {
  console.log('🔄 Refreshing access token...');
  
  const API_BASE_URL = getApiBaseUrl();
  
  try {
    const response = await axios.post(`${API_BASE_URL}/refresh`, {
      refresh_token: refreshToken,
    });

    console.log('📦 Refresh Response:', response.data);

    const { success, access_token, refresh_token: newRefreshToken } = response.data;
    
    // Check if API returned error
    if (!success) {
      console.error('❌ API returned error');
      throw new AuthError('Token refresh failed. Please login again.');
    }
    
    // Validate tokens exist
    if (!access_token || !newRefreshToken) {
      console.error('❌ Missing tokens in refresh response:', {
        hasAccessToken: !!access_token,
        hasRefreshToken: !!newRefreshToken,
        success,
        fullResponse: response.data
      });
      throw new AuthError('Token refresh failed: Missing tokens in server response. Please login again.');
    }

    // Validate token format
    if (typeof access_token !== 'string' || typeof newRefreshToken !== 'string') {
      console.error('❌ Invalid token format in refresh response');
      throw new AuthError('Token refresh failed: Invalid token format. Please login again.');
    }

    // Validate token is not empty
    if (access_token.trim() === '' || newRefreshToken.trim() === '') {
      console.error('❌ Empty token value in refresh response');
      throw new AuthError('Token refresh failed: Empty token received. Please login again.');
    }

    // Save new tokens in cookies
    Cookies.set('accessToken', access_token, COOKIE_OPTIONS);
    Cookies.set('refreshToken', newRefreshToken, COOKIE_OPTIONS);
    
    console.log('✅ Token refreshed successfully');
    return response;
  } catch (error: any) {
    console.error('❌ Token refresh failed:', error.response?.data || error.message);
    
    if (error instanceof AuthError) {
      throw error;
    }

    // Handle axios errors
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;
      
      // Check if API returned error in response body
      if (responseData?.error) {
        throw new AuthError(
          responseData.error || 'Token refresh failed. Please login again.',
          error.response?.status,
          error
        );
      }
      
      const errorMessage = responseData?.error || 'Session expired. Please login again.';
      throw new AuthError(errorMessage, error.response?.status, error);
    }
    
    throw new AuthError(
      error.message || 'Token refresh failed. Please login again.',
      undefined,
      error
    );
  }
};

export const logout = async () => {
  console.log('👋 Logging out...');
  
  // Stateless logout - just clear cookies on client side
  try {
    Cookies.remove('accessToken', COOKIE_OPTIONS);
    Cookies.remove('refreshToken', COOKIE_OPTIONS);
    
    console.log('✅ Logged out successfully (stateless)');
    return { success: true };
  } catch (error) {
    console.error('❌ Logout error:', error);
    // Always clear tokens on logout attempt
    Cookies.remove('accessToken', COOKIE_OPTIONS);
    Cookies.remove('refreshToken', COOKIE_OPTIONS);
    throw error;
  }
};

// Optional: If you want to validate user session on app load
// Optional: If you want to validate user session on app load
export const validateSession = async () => {
  const accessToken = Cookies.get('accessToken');
  const refreshTokenValue = Cookies.get('refreshToken'); // ← Renamed variable
  
  if (!accessToken || !refreshTokenValue) {
    return false;
  }
  
  // Check if access token is expired
  try {
    const tokenParts = accessToken.split('.');
    if (tokenParts.length !== 3) return false;
    
    const payload = JSON.parse(atob(tokenParts[1]));
    if (!payload.exp) return false;
    
    const isExpired = Date.now() >= payload.exp * 1000;
    
    if (isExpired) {
      // Try to refresh
      try {
        await refreshToken(refreshTokenValue); // ← Now calls the function correctly
        return true;
      } catch {
        return false;
      }
    }
    
    return true;
  } catch {
    return false;
  }
};